/**
 * ============================================================================
 * PMTaskCard — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: card view for a single PM task. Mirrors the CalibrationTaskCard
 *          layout (mobile-friendly, criticality chip, days-until pill,
 *          last-PM hint, "Record PM" CTA).
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './PMTray.module.css';
import type { PMTask } from './usePMTrayQuery';
import type { EquipmentCriticality } from '../EquipmentWorkbench/useEquipmentQuery';

const CRIT_CLASS: Record<EquipmentCriticality, string> = {
  CRITICAL: styles.critCritical,
  MAJOR: styles.critMajor,
  MINOR: styles.critMinor,
  NON_GMP: styles.critNonGmp,
};

export interface PMTaskCardProps {
  task: PMTask;
  onRecord: (task: PMTask) => void;
}

export function PMTaskCard({ task, onRecord }: PMTaskCardProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const d = task.days_until_due;
  const completed = task.bucket === 'RECENTLY_COMPLETED';
  let pillClass: string;
  let pillLabel: string;
  if (completed) {
    pillClass = styles.dueDone;
    pillLabel = t('equipment.pmTray.completed', { defaultValue: 'Completed' });
  } else if (d < 0) {
    pillClass = styles.dueOverdue;
    pillLabel = t('equipment.pmTray.overdueDays', {
      defaultValue: '{{n}} days overdue',
      n: nf.format(Math.abs(d)),
    });
  } else if (d <= 7) {
    pillClass = styles.dueSoon;
    pillLabel = t('equipment.pmTray.dueIn', {
      defaultValue: 'Due in {{n}} days',
      n: nf.format(d),
    });
  } else {
    pillClass = styles.dueLater;
    pillLabel = t('equipment.pmTray.dueIn', {
      defaultValue: 'Due in {{n}} days',
      n: nf.format(d),
    });
  }

  return (
    <article
      className={styles.card}
      aria-label={t('equipment.pmTray.cardAria', {
        defaultValue: 'PM task for {{tag}} ({{name}})',
        tag: task.equipment_tag,
        name: task.equipment_name,
      })}
    >
      <div className={styles.cardMain}>
        <div className={styles.cardTitleRow}>
          <code className={styles.tagMono}>{task.equipment_tag}</code>
          <h3 className={styles.cardName}>{task.equipment_name}</h3>
          <span className={`${styles.chip} ${CRIT_CLASS[task.criticality]}`}>
            {t(`equipment.criticality.${task.criticality}`, {
              defaultValue: task.criticality,
            })}
          </span>
          <span className={`${styles.duePill} ${pillClass}`}>{pillLabel}</span>
        </div>
        <div className={styles.cardMeta}>
          <span>
            {t('equipment.pmTray.due', {
              defaultValue: 'Due: {{ts}}',
              ts: dateFmt.format(new Date(task.due_at_utc)),
            })}
          </span>
          <span>
            {task.last_pm_at_utc
              ? t('equipment.pmTray.lastPm', {
                  defaultValue: 'Last PM: {{ts}}',
                  ts: dateFmt.format(new Date(task.last_pm_at_utc)),
                })
              : t('equipment.pmTray.lastPmNone', {
                  defaultValue: 'Last PM: never',
                })}
          </span>
          {task.engineer_hint && (
            <span>
              {t('equipment.pmTray.engineer', {
                defaultValue: 'Engineer: {{name}}',
                name: task.engineer_hint,
              })}
            </span>
          )}
        </div>
      </div>
      <div className={styles.cardActions}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onRecord(task)}
          disabled={completed}
          aria-label={t('equipment.pmTray.recordAria', {
            defaultValue: 'Record PM for {{tag}}',
            tag: task.equipment_tag,
          })}
        >
          {t('equipment.pmTray.record', { defaultValue: 'Record PM' })}
        </Button>
      </div>
    </article>
  );
}
